import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Shield, Loader2, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { readCheckoutResume, updateCheckoutResume } from '@/pages/order/checkoutResume';

export default function MagicLinkPage() {
  const [params] = useSearchParams();
  const { consumeMagicLink } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const consumedRef = useRef(false);

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setError('No magic link token found.');
      return;
    }
    // Guard against React StrictMode double-mount consuming the token twice
    if (consumedRef.current) return;
    consumedRef.current = true;

    consumeMagicLink(token)
      .then((user) => {
        const checkoutResume = readCheckoutResume();
        if (checkoutResume?.flow === 'checkout' && checkoutResume?.return_to === '/order/checkout') {
          updateCheckoutResume({
            awaiting_magic_link: false,
            resumed_via: 'magic_link',
            needs_password_setup: !user.has_password,
          });
          navigate('/order/checkout', { replace: true });
          return;
        }
        // Slice 7.4: honor a same-origin ?redirect= that survived the email round-trip.
        const raw = params.get('redirect');
        let safeRedirectTo = null;
        if (raw && raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/\\')) {
          safeRedirectTo = raw;
        }
        if (safeRedirectTo) {
          navigate(safeRedirectTo, { replace: true });
          return;
        }
        navigate((user.account_type === 'staff' || user.role === 'admin') ? '/team' : '/account', { replace: true });
      })
      .catch((err) => {
        setError(err.response?.data?.detail || 'Invalid or expired magic link.');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg-paper))' }}>
      <div className="text-center">
        <Shield className="h-8 w-8 mx-auto mb-3 text-ink" strokeWidth={1.5} />
        {error ? (
          <>
            <h1 className="font-display text-xl font-medium text-ink mb-2" data-testid="magic-link-error-title">
              This link didn&apos;t work
            </h1>
            <p className="text-sm text-muted-foreground mb-1 max-w-xs mx-auto">
              {error}
            </p>
            <p className="text-xs text-muted-foreground mb-4 max-w-xs mx-auto leading-relaxed">
              Magic links can only be used once and expire after 15 minutes. Request a fresh one and we&apos;ll email you a new link.
            </p>
            <Link to="/account/sign-in">
              <Button size="sm" className="gap-2 rounded-[12px]" data-testid="magic-link-request-new-btn">
                <Mail className="h-4 w-4" /> Request a New Link
              </Button>
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="h-6 w-6 mx-auto mb-3 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Signing you in...</p>
          </>
        )}
      </div>
    </div>
  );
}
